import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FeatureChecklist = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);

  const features = [
    {
      id: 1,
      icon: "Printer",
      title: "Receipt Printing Integration",
      description: "Seamless integration with thermal and standard printers for instant receipt generation",
      details: "Supports multiple printer models including thermal, dot matrix, and laser printers. Customizable receipt templates with logo, business details, and promotional messages. Automatic paper size detection and print queue management."
    },
    {
      id: 2,
      icon: "Scan",
      title: "Barcode Scanning Capabilities",
      description: "Fast product lookup and inventory management with barcode scanner support",
      details: "Compatible with USB and Bluetooth barcode scanners. Supports multiple barcode formats including EAN-13, UPC-A, Code 128, and QR codes. Instant product identification and price retrieval for faster checkout."
    },
    {
      id: 3,
      icon: "Package",
      title: "Local Inventory Management",
      description: "Complete inventory control without internet dependency",
      details: "Real-time stock level tracking with low stock alerts. Batch product updates and category management. Product variants support with size, color, and custom attributes. Automatic reorder point calculations."
    },
    {
      id: 4,
      icon: "Database",
      title: "Local Data Backup",
      description: "Automatic daily backups to protect your business data",
      details: "Scheduled automatic backups with customizable frequency. Multiple backup locations including external drives and network storage. One-click data restoration with version history. Encrypted backup files for security."
    },
    {
      id: 9,
      icon: "Key",
      title: "License Management & Enforcement",
      description: "View and manage license details, expiry and allowed terminals. License controls which features are enabled and enforces limits.",
      details: "License types (Trial, Basic, Pro, Enterprise) encode feature flags and quotas (e.g., max products, client count). When a license expires: POS checkout remains available but reporting, analytics and editing are restricted or read-only depending on rules. License activation, renewal and payments are handled via Paystack or bank transfer."
    },
    {
      id: 10,
      icon: "Lock",
      title: "Authentication & Permissions",
      description: "PIN-based staff login and role-based access control",
      details: "Staff login uses a 6-digit PIN. Permissions are role-based (Admin, Manager, Staff) and enforced on every page. Admins can manage staff, roles and backup/restore. Audit trails record activity."
    },
    {
      id: 5,
      icon: "RefreshCw",
      title: "Manual Cloud Synchronization",
      description: "Sync data to cloud when internet is available for multi-store management",
      details: "On-demand sync with conflict resolution. Selective data synchronization for bandwidth optimization. Sync status monitoring with detailed logs. Automatic retry for failed sync attempts."
    },
    {
      id: 6,
      icon: "ShoppingCart",
      title: "Offline Transaction Processing",
      description: "Process sales, returns, and exchanges without internet connection",
      details: "Complete POS functionality including split payments, discounts, and tax calculations. Customer account management with credit tracking. Hold and recall transactions for later completion. Multiple payment method support."
    },
    {
      id: 7,
      icon: "FileText",
      title: "Comprehensive Reporting",
      description: "Generate detailed sales and inventory reports locally",
      details: "Daily, weekly, and monthly sales reports with graphical visualization. Product performance analysis and best-seller identification. Staff performance tracking and commission calculations. Customizable report templates and export options."
    },
    {
      id: 8,
      icon: "Users",
      title: "Multi-User Access Control",
      description: "Role-based permissions for staff management",
      details: "Customizable user roles with granular permissions. Activity logging for audit trails. Shift management with clock-in/clock-out tracking. Individual user sales performance monitoring."
    }
  ];

  const toggleFeature = (featureId) => {
    setExpandedFeature(expandedFeature === featureId ? null : featureId);
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Complete Offline POS Features
          </h2>
          <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to run your retail business without internet dependency. Click any feature to learn more about its capabilities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto">
          {features?.map((feature) => (
            <div
              key={feature?.id}
              className="bg-card rounded-xl border border-border shadow-purple-sm transition-smooth hover:shadow-purple-md overflow-hidden"
            >
              <button
                onClick={() => toggleFeature(feature?.id)}
                className="w-full p-6 md:p-8 text-left flex items-start gap-4 md:gap-6 transition-smooth hover:bg-muted focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              >
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-lg">
                  <Icon name={feature?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
                      {feature?.title}
                    </h3>
                    <Icon 
                      name={expandedFeature === feature?.id ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      color="var(--color-muted-foreground)" 
                      strokeWidth={2}
                      className="flex-shrink-0 transition-smooth"
                    />
                  </div>
                  <p className="font-body text-sm md:text-base text-muted-foreground">
                    {feature?.description}
                  </p>
                </div>
              </button>

              {expandedFeature === feature?.id && (
                <div className="px-6 md:px-8 pb-6 md:pb-8 pt-0">
                  <div className="pl-16 md:pl-20 border-l-2 border-primary/20">
                    <p className="font-body text-sm md:text-base text-foreground leading-relaxed">
                      {feature?.details}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-flex items-center justify-center px-4 md:px-6 py-3 md:py-4 bg-success/10 rounded-xl border border-success/20">
            <Icon name="CheckCircle" size={24} color="var(--color-success)" strokeWidth={2} className="mr-3" />
            <span className="font-body text-sm md:text-base font-medium text-success">
              All features work 100% offline with no internet required
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureChecklist;